import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";

import { ActionsForRole } from "@/api/admin/roleManagment";
import { AdminSections } from "@/api/admin/teamRoles";
import { FormCardBasic, Loader } from "@/components/atoms";
import { toastSuccess } from "@/components/molecules";
import { isMenuReadyToSave } from "@/components/units/BrandFormMenu/menuMappers";
import { PATHS } from "@/config/path";
import {
  clearWizardDraft,
  NEW_WIZARD_DRAFT_ENTITY_ID,
  readWizardDraft,
} from "@/modules/wizardDraft";
import {
  BRAND_WIZARD_RETURN_TO_KEY,
  ensureWizardReturnTo,
} from "@/modules/wizardReturnTo";
import { PermissionGuard } from "@/services/casl/PermissionGuard";
import { usePermission } from "@/services/casl/usePermission";
import { handleErrorToast } from "@/utils/handleErrorToast";

import { BrandWizardProvider } from "./BrandWizardContext";
import {
  useBrandContext,
  useBrandRouter,
  useBrandSnapshot,
  useDemoBrandStore,
} from "./hooks";

const BrandStepsAdmin = observer(() => {
  const hasReadPermission = usePermission(AdminSections.BRAND);
  const { groupId, brandId, hasFixedGroupId } = useBrandContext();
  const [searchParams] = useSearchParams();
  const queryGroupId = searchParams.get("groupId");

  const isPureCreate = !brandId && !groupId && !queryGroupId;

  const snapshot = useBrandSnapshot();

  const finalGroupId = hasFixedGroupId ? groupId : snapshot?.groupId;
  const finalBrandId = snapshot?.brandId || brandId;

  // Keep entry return path across reload; seed fallback only if missing
  useEffect(() => {
    const fallback = finalGroupId
      ? PATHS.ADMIN.ACCOUNT_GROUP_DETAILS(finalGroupId)
      : finalBrandId
        ? PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(finalBrandId)
        : PATHS.ADMIN.ACCOUNT;
    ensureWizardReturnTo(BRAND_WIZARD_RETURN_TO_KEY, fallback);
  }, [finalGroupId, finalBrandId]);

  const stepPaths = useMemo(
    () => [
      PATHS.ADMIN.BRAND_WIZARD_DETAILS(brandId),
      PATHS.ADMIN.BRAND_WIZARD_MENU(brandId),
      PATHS.ADMIN.BRAND_WIZARD_CONTACTS(brandId),
      PATHS.ADMIN.BRAND_WIZARD_BRANCHES(brandId),
    ],
    [brandId]
  );

  const brandStore = useDemoBrandStore(
    finalGroupId,
    finalBrandId,
    hasFixedGroupId
  );
  const draftEntityId = finalBrandId ?? NEW_WIZARD_DRAFT_ENTITY_ID;
  const [isWizardHydrated, setIsWizardHydrated] = useState(false);

  useEffect(() => {
    if (isWizardHydrated || brandStore.isFetching) {
      return;
    }

    const draft = readWizardDraft("brand", draftEntityId);
    if (draft) {
      brandStore.setAll(draft.steps);
    }

    setIsWizardHydrated(true);
  }, [brandStore, brandStore.isFetching, draftEntityId, isWizardHydrated]);

  const router = useBrandRouter(brandStore, stepPaths);

  const handleSubmit = useCallback(async () => {
    try {
      let createdEntityId: string | undefined;

      if (!brandStore.details?.id) {
        const created = await brandStore.createBrand(!hasReadPermission);
        createdEntityId = created.id!;
        clearWizardDraft("brand", NEW_WIZARD_DRAFT_ENTITY_ID);
        router.setBrandId(createdEntityId);
        toastSuccess(`Brand ${created.nameEn} created successfully!`);

        if (hasReadPermission) {
          await brandStore.fetchBrand(createdEntityId);
        }
      } else {
        const updated = await brandStore.updateBrand();
        toastSuccess(`Brand ${updated.nameEn} updated successfully!`);
      }

      if (brandStore.details?.id && isMenuReadyToSave(brandStore.menu)) {
        await brandStore.upsertMenu();
      }

      return createdEntityId ?? true;
    } catch (error) {
      handleErrorToast(error, () => handleSubmit());
      return false;
    }
  }, [brandStore, hasReadPermission, router]);

  const contextValue = useMemo(
    () => ({
      brandStore,
      router,
      onSubmit: handleSubmit,
    }),
    [brandStore, router, handleSubmit]
  );

  const content = isWizardHydrated ? (
    <BrandWizardProvider value={contextValue}>
      <Outlet />
    </BrandWizardProvider>
  ) : (
    <FormCardBasic>
      <Loader />
    </FormCardBasic>
  );

  if (isPureCreate) {
    return (
      <PermissionGuard
        action={ActionsForRole.UPDATE}
        subject={AdminSections.BRANDS_LIST}
      >
        {content}
      </PermissionGuard>
    );
  }

  return content;
});

export default BrandStepsAdmin;
